import { createContext, ReactNode, useContext, useMemo } from "react";
import type { AdminIdentity } from "../api/types";

export type GatewayPermission =
  | "overview.read"
  | "models.read"
  | "models.write"
  | "providers.read"
  | "providers.write"
  | "providers.credentials"
  | "consumers.read"
  | "consumers.write"
  | "circuits.read"
  | "circuits.write"
  | "requests.read"
  | "costs.read"
  | "playground.generate";

const allPermissions: GatewayPermission[] = [
  "overview.read",
  "models.read",
  "models.write",
  "providers.read",
  "providers.write",
  "providers.credentials",
  "consumers.read",
  "consumers.write",
  "circuits.read",
  "circuits.write",
  "requests.read",
  "costs.read",
  "playground.generate",
];

// playground.generate is granted to every console role (D4) but stays a separate point so it can be narrowed later.
// playground.generate 目前授予全部控制台角色（D4），保留独立权限点便于以后收紧。
const rolePermissions: Record<string, GatewayPermission[]> = {
  gateway_admin: allPermissions,
  gateway_operator: ["overview.read", "models.read", "models.write", "providers.read", "providers.write", "circuits.read", "circuits.write", "requests.read", "costs.read", "playground.generate"],
  gateway_finance: ["overview.read", "requests.read", "costs.read", "playground.generate"],
  gateway_viewer: ["overview.read", "models.read", "providers.read", "circuits.read", "requests.read", "costs.read", "playground.generate"],
};

export const roleLabels: Record<string, string> = {
  gateway_admin: "网关管理员",
  gateway_operator: "网关运维",
  gateway_finance: "成本查看",
  gateway_viewer: "只读成员",
};

// identityRoles normalizes Keeper application roles for permission checks.
// 统一整理 Keeper 应用角色供权限判断使用。
export function identityRoles(identity: AdminIdentity | null | undefined): string[] {
  const source = identity?.roles?.length ? identity.roles : identity?.role ? [identity.role] : [];
  return Array.from(new Set(source.map((role) => role.trim()).filter(Boolean)));
}

// permissionsForIdentity merges permissions when Keeper assigns more than one app role.
// 将 Keeper 分配的多个应用角色合并成当前用户权限。
export function permissionsForIdentity(identity: AdminIdentity | null | undefined): Set<GatewayPermission> {
  const permissions = new Set<GatewayPermission>();
  identityRoles(identity).forEach((role) => rolePermissions[role]?.forEach((permission) => permissions.add(permission)));
  return permissions;
}

interface GatewayPermissionContextValue {
  identity: AdminIdentity;
  roles: string[];
  can: (permission: GatewayPermission) => boolean;
}

const GatewayPermissionContext = createContext<GatewayPermissionContextValue | null>(null);

// GatewayPermissionProvider shares one role interpretation across routes and action controls.
// 给路由、菜单和操作按钮提供同一套角色判断。
export function GatewayPermissionProvider({ identity, children }: { identity: AdminIdentity; children: ReactNode }) {
  const value = useMemo<GatewayPermissionContextValue>(() => {
    const roles = identityRoles(identity);
    const permissions = permissionsForIdentity(identity);
    return { identity, roles, can: (permission) => permissions.has(permission) };
  }, [identity]);
  return <GatewayPermissionContext.Provider value={value}>{children}</GatewayPermissionContext.Provider>;
}

// useGatewayPermissions reads the current Keeper identity and permission checker.
// 读取当前 Keeper 身份和权限判断方法。
export function useGatewayPermissions(): GatewayPermissionContextValue {
  const value = useContext(GatewayPermissionContext);
  if (!value) throw new Error("GatewayPermissionProvider is missing");
  return value;
}
